/**
 * Security utilities for XSS prevention and CSP compliance
 */

/**
 * Sanitize HTML content to prevent XSS attacks
 */
export function sanitizeHTML(html: string): string {
  const div = document.createElement('div');
  div.textContent = html;
  return div.innerHTML;
}

/**
 * Sanitize text content for safe display
 */
export function sanitizeText(text: string): string {
  // Remove potentially dangerous characters
  return text
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/&/g, '&amp;');
}

/**
 * Create safe CSS styles without violating CSP
 */
export function createSafeStyles(styles: string): HTMLStyleElement {
  const style = document.createElement('style');
  style.textContent = styles;
  return style;
}

/**
 * Validate CSS selector to prevent injection
 */
export function isValidSelector(selector: string): boolean {
  try {
    document.querySelector(selector);
    return true;
  } catch {
    return false;
  }
}

/**
 * Escape CSS property values
 */
export function escapeCSS(value: string): string {
  return value.replace(/["\\]/g, '\\$&').replace(/[^a-zA-Z0-9\-_#\.]/g, '');
}

/**
 * Create safe element with sanitized content
 */
export function createSafeElement(
  tag: string,
  content: string,
  attributes: Record<string, string> = {},
): HTMLElement {
  const element = document.createElement(tag);

  // Set attributes safely
  Object.entries(attributes).forEach(([key, value]) => {
    if (key.startsWith('data-') || key === 'class' || key === 'id') {
      element.setAttribute(key, sanitizeText(value));
    }
  });

  // Set content safely
  if (content) {
    element.textContent = sanitizeText(content);
  }

  return element;
}

/**
 * Check if URL is safe for navigation
 */
export function isSafeURL(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    return (
      parsed.protocol === 'http:' || parsed.protocol === 'https:' || parsed.protocol === 'mailto:'
    );
  } catch {
    return false;
  }
}
